
import React, { useEffect, useRef, useState } from "react";

import {
  Mail,
  Phone,
  MapPin,
  Send,
  ArrowUpRight,
  Clock3,
  MessageCircle,
  CheckCircle2,
  ShieldCheck,
  Rocket,
  CircleHelp,
  CalendarDays,
  Code2,
  BrainCircuit,
  Bot,
  Layers3,
} from "lucide-react";

import emailjs from "@emailjs/browser";

import { Link } from "react-router-dom";

import "./Contact.css";

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  /* =====================================================
     SCROLL ANIMATION
  ===================================================== */

  const heroRef = useRef(null);
  const mainRef = useRef(null);
  const benefitsRef = useRef(null);
  const processRef = useRef(null);
  const helpRef = useRef(null);
  const finalRef = useRef(null);

  const [visibleSections, setVisibleSections] = useState({
    main: false,
    benefits: false,
    process: false,
    help: false,
    final: false,
  });

  useEffect(() => {
    const sections = [
      mainRef.current,
      benefitsRef.current,
      processRef.current,
      helpRef.current,
      finalRef.current,
    ].filter(Boolean);

    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionKey = entry.target.dataset.animationSection;

            setVisibleSections((previous) => ({
              ...previous,
              [sectionKey]: true,
            }));

            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  /* =====================================================
     HANDLE INPUT
  ===================================================== */

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  /* =====================================================
     SUBMIT ENQUIRY - EMAILJS
  ===================================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setSubmitted(false);

    try {
      await emailjs.send(
        "service_keejhll",
        "template_182uheo",
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          message: formData.message,
        },
        "LClupYxWr5n8KJhCl"
      );

      setSubmitted(true);

      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
      });
    } catch (error) {
      console.error("Enquiry submission error:", error);

      alert(
        "Sorry, your enquiry could not be submitted. Please try again or contact us directly."
      );
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     BENEFITS
  ===================================================== */

  const benefits = [
    {
      icon: <MessageCircle size={22} />,
      title: "Clear Communication",
      text:
        "We keep communication simple, transparent and focused from the first conversation to delivery.",
    },
    {
      icon: <Code2 size={22} />,
      title: "Practical Solutions",
      text:
        "We focus on solving the actual business problem instead of adding unnecessary technology.",
    },
    {
      icon: <ShieldCheck size={22} />,
      title: "Reliable Development",
      text:
        "Our solutions are planned with maintainability, scalability and long-term usability in mind.",
    },
    {
      icon: <Rocket size={22} />,
      title: "Built to Grow",
      text:
        "We create digital products that can evolve with your business, users and future requirements.",
    },
  ];

  /* =====================================================
     PROCESS
  ===================================================== */

  const process = [
    {
      number: "01",
      icon: <MessageCircle size={20} />,
      title: "Tell us about your idea",
      text:
        "Share your business goals, requirements, challenges or the product you have in mind.",
    },
    {
      number: "02",
      icon: <BrainCircuit size={20} />,
      title: "Understand the requirement",
      text:
        "We review your requirements and identify the right technology, features and development approach.",
    },
    {
      number: "03",
      icon: <CalendarDays size={20} />,
      title: "Discuss the solution",
      text:
        "We discuss scope, development approach and the next steps for your project.",
    },
    {
      number: "04",
      icon: <Rocket size={20} />,
      title: "Start building",
      text:
        "Once everything is aligned, we move forward with design, development and implementation.",
    },
  ];

  return (
    <div className="contact-page">

      {/* =====================================================
          HERO
      ===================================================== */}

      <section
        ref={heroRef}
        className="contact-hero contact-hero-visible"
      >
        <div className="contact-hero-grid"></div>

        <div className="contact-container contact-hero-container">

          <div className="contact-hero-content">

            <div className="contact-label">
              <span></span>
              CONTACT NEXIS FOLD
            </div>

            <h1>
              Let's talk about
              <br />
              <span>your next project.</span>
            </h1>

            <p>
              Have a business idea, software requirement or
              automation challenge? Tell us what you want to
              build and let's discuss the right technology
              solution for your business.
            </p>

            <div className="contact-hero-actions">

              <a
                href="#project-enquiry"
                className="contact-primary-btn"
              >
                Start a Conversation
                <ArrowUpRight size={17} />
              </a>

              <a
                href="tel:+919022590056"
                className="contact-secondary-btn"
              >
                <Phone size={16} />
                Call Us
              </a>

            </div>

            <div className="contact-hero-trust">

              <div>
                <CheckCircle2 size={16} />
                No-obligation discussion
              </div>

              <div>
                <CheckCircle2 size={16} />
                Practical advice
              </div>

              <div>
                <CheckCircle2 size={16} />
                Clear next steps
              </div>

            </div>

          </div>

          {/* HERO PANEL */}

          <div className="contact-hero-panel">

            <div className="contact-panel-header">
              <span>NEXIS FOLD</span>
              <span>CONTACT</span>
            </div>

            <div className="contact-panel-content">

              <div className="contact-panel-icon">
                <MessageCircle
                  size={35}
                  strokeWidth={1.6}
                />
              </div>

              <h3>
                Let's build
                <br />
                <span>something useful.</span>
              </h3>

              <p>
                Start with an idea, a problem or a
                business requirement. We'll help you
                identify the right direction.
              </p>

            </div>

            <div className="contact-panel-services">

              <div>
                <Code2 size={16} />
                <span>Software Development</span>
              </div>

              <div>
                <Bot size={16} />
                <span>AI & Automation</span>
              </div>

              <div>
                <Layers3 size={16} />
                <span>Digital Products</span>
              </div>

            </div>

            <div className="contact-panel-footer">
              <span>01</span>

              <div></div>

              <span>BUILD · AUTOMATE · GROW</span>
            </div>

          </div>

        </div>
      </section>


      {/* =====================================================
          CONTACT MAIN
      ===================================================== */}

      <section
        ref={mainRef}
        data-animation-section="main"
        className={`contact-main-section ${
          visibleSections.main
            ? "contact-section-visible"
            : ""
        }`}
        id="project-enquiry"
      >

        <div className="contact-container">

          <div className="contact-main-heading">

            <div>

              <span className="contact-section-label">
                START A CONVERSATION
              </span>

              <h2>
                Tell us what
                <span> you're building.</span>
              </h2>

            </div>

            <p>
              You don't need to have everything figured out.
              Give us the basic idea and we'll help you
              understand the next step.
            </p>

          </div>


          <div className="contact-main-grid">

            {/* LEFT INFORMATION */}

            <div className="contact-information">

              <div className="contact-info-intro">

                <div className="contact-info-number">
                  01
                </div>

                <div>
                  <h3>
                    Let's discuss your project.
                  </h3>

                  <p>
                    Whether you're starting from scratch,
                    improving an existing product or
                    automating a business process, we're
                    ready to listen.
                  </p>
                </div>

              </div>


              <div className="contact-info-box">

                <div className="contact-info-box-top">
                  <span>HOW WE CAN HELP</span>
                  <span>NF / 01</span>
                </div>

                <div className="contact-info-box-main">

                  <div>
                    <Code2 size={21} />
                    <span>Web & Software</span>
                  </div>

                  <div>
                    <Bot size={21} />
                    <span>AI & Automation</span>
                  </div>

                  <div>
                    <Layers3 size={21} />
                    <span>Custom Solutions</span>
                  </div>

                </div>

                <p>
                  We work with businesses to design,
                  develop and improve practical digital
                  solutions.
                </p>

              </div>


              {/* CONTACT DETAILS */}

              <div className="contact-details">

                <a
                  href="mailto:nexisfold@gmail.com"
                  className="contact-detail"
                >

                  <div className="contact-detail-icon">
                    <Mail size={19} />
                  </div>

                  <div>
                    <span>EMAIL</span>

                    <strong>
                      nexisfold@gmail.com
                    </strong>

                    <small>
                      Send your requirements anytime.
                    </small>
                  </div>

                  <ArrowUpRight
                    size={16}
                    className="contact-detail-arrow"
                  />

                </a>


                <a
                  href="tel:+919022590056"
                  className="contact-detail"
                >

                  <div className="contact-detail-icon">
                    <Phone size={19} />
                  </div>

                  <div>
                    <span>PHONE</span>

                    <strong>
                      +91 90225 90056
                    </strong>

                    <small>
                      Speak directly with our team.
                    </small>
                  </div>

                  <ArrowUpRight
                    size={16}
                    className="contact-detail-arrow"
                  />

                </a>


                <a
                  href="tel:+917249022008"
                  className="contact-detail"
                >

                  <div className="contact-detail-icon">
                    <Phone size={19} />
                  </div>

                  <div>
                    <span>PHONE</span>

                    <strong>
                      +91 72490 22008
                    </strong>

                    <small>
                      Speak directly with our team.
                    </small>
                  </div>

                  <ArrowUpRight
                    size={16}
                    className="contact-detail-arrow"
                  />

                </a>


                <div className="contact-detail">

                  <div className="contact-detail-icon">
                    <MapPin size={19} />
                  </div>

                  <div>
                    <span>LOCATION</span>

                    <strong>
                      Pune, India
                    </strong>

                    <small>
                      Working with businesses across locations.
                    </small>
                  </div>

                </div>


                <div className="contact-detail">

                  <div className="contact-detail-icon">
                    <Clock3 size={19} />
                  </div>

                  <div>
                    <span>RESPONSE TIME</span>

                    <strong>
                      Within 1 business day
                    </strong>

                    <small>
                      Every enquiry is reviewed carefully.
                    </small>
                  </div>

                </div>

              </div>


              {/* DIRECT CALL */}

              <a
                href="tel:+919022590056"
                className="contact-direct-call"
              >

                <div>
                  <Phone size={17} />
                </div>

                <span>
                  <small>PREFER TO TALK?</small>
                  <strong>Call us directly</strong>
                </span>

                <ArrowUpRight size={18} />

              </a>

            </div>


            {/* FORM */}

            <div className="contact-form-card">

              <div className="contact-form-top">

                <div>

                  <span className="contact-section-label">
                    PROJECT ENQUIRY
                  </span>

                  <h2>
                    How can we
                    <span> help?</span>
                  </h2>

                </div>

                <div className="contact-form-code">
                  NF / 01
                </div>

              </div>


              <p className="contact-form-description">
                Tell us a little about your project and
                we'll get back to you with the next steps.
              </p>


              {/* SUCCESS */}

              {submitted && (

                <div className="contact-success-message">

                  <div className="contact-success-icon">
                    <CheckCircle2 size={23} />
                  </div>

                  <div>

                    <strong>
                      Enquiry submitted successfully.
                    </strong>

                    <p>
                      Thank you for contacting Nexis Fold.
                      We'll review your requirements and
                      get back to you soon.
                    </p>

                  </div>

                </div>

              )}


              <form onSubmit={handleSubmit}>

                <div className="contact-form-row">

                  <div className="contact-field">

                    <label htmlFor="name">
                      Full Name *
                    </label>

                    <input
                      id="name"
                      type="text"
                      name="name"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />

                  </div>


                  <div className="contact-field">

                    <label htmlFor="email">
                      Email Address *
                    </label>

                    <input
                      id="email"
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />

                  </div>

                </div>


                <div className="contact-form-row">

                  <div className="contact-field">

                    <label htmlFor="phone">
                      Phone Number
                    </label>

                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      placeholder="+91 9022590056"
                      value={formData.phone}
                      onChange={handleChange}
                    />

                  </div>


                  <div className="contact-field">

                    <label htmlFor="service">
                      Required Service *
                    </label>

                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                    >

                      <option value="">
                        Select a service
                      </option>

                      <option value="web-development">
                        Web Development
                      </option>

                      <option value="mobile-development">
                        Mobile App Development
                      </option>

                      <option value="ui-ux">
                        UI/UX Design
                      </option>

                      <option value="backend">
                        Backend Development
                      </option>

                      <option value="ai-machine-learning">
                        AI & Machine Learning
                      </option>

                      <option value="ai-automation">
                        AI & Automation
                      </option>

                      <option value="cloud-devops">
                        Cloud & DevOps
                      </option>

                      <option value="ecommerce">
                        E-Commerce Development
                      </option>

                      <option value="custom-software">
                        Custom Software
                      </option>

                    </select>

                  </div>

                </div>


                <div className="contact-field contact-message-field">

                  <label htmlFor="message">
                    Project Details *
                  </label>

                  <textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your idea, required features, target users, current problem or anything else that would help us understand your project..."
                    value={formData.message}
                    onChange={handleChange}
                    rows="7"
                    required
                  />

                  <span className="contact-character-hint">
                    Give us as much detail as you are comfortable sharing.
                  </span>

                </div>


                <div className="contact-form-note">

                  <ShieldCheck size={17} />

                  <span>
                    Your project information is used only
                    to understand your requirements and
                    respond to your enquiry.
                  </span>

                </div>


                <button
                  type="submit"
                  className="contact-submit-btn"
                  disabled={loading}
                >

                  <span>
                    {loading
                      ? "Sending Enquiry..."
                      : "Send Project Enquiry"}
                  </span>

                  {!loading && (
                    <Send size={17} />
                  )}

                </button>


                <p className="contact-form-bottom">
                  We'll review your enquiry and contact you
                  with the next steps.
                </p>

              </form>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          WHY NEXIS FOLD
      ===================================================== */}

      <section
        ref={benefitsRef}
        data-animation-section="benefits"
        className={`contact-benefits-section ${
          visibleSections.benefits
            ? "contact-section-visible"
            : ""
        }`}
      >

        <div className="contact-container">

          <div className="contact-section-heading">

            <div>

              <span className="contact-section-label">
                WHY NEXIS FOLD
              </span>

              <h2>
                More than just
                <span> development.</span>
              </h2>

            </div>

            <p>
              We approach every project as a business
              problem first and a technology problem second.
            </p>

          </div>


          <div className="contact-benefits-grid">

            {benefits.map((benefit, index) => (

              <div
                className={`contact-benefit-card contact-benefit-card-${index + 1}`}
                key={index}
              >

                <div className="contact-benefit-top">

                  <div className="contact-benefit-icon">
                    {benefit.icon}
                  </div>

                  <span>
                    0{index + 1}
                  </span>

                </div>

                <h3>
                  {benefit.title}
                </h3>

                <p>
                  {benefit.text}
                </p>

              </div>

            ))}

          </div>

        </div>

      </section>


      {/* =====================================================
          WHAT HAPPENS NEXT
      ===================================================== */}

      <section
        ref={processRef}
        data-animation-section="process"
        className={`contact-process-section ${
          visibleSections.process
            ? "contact-section-visible"
            : ""
        }`}
      >

        <div className="contact-container">

          <div className="contact-process-heading">

            <span className="contact-section-label">
              WHAT HAPPENS NEXT
            </span>

            <h2>
              From first message
              <span> to build.</span>
            </h2>

            <p>
              Starting a technology project should not feel
              complicated. We keep the initial conversation
              simple and focused on your actual requirements.
            </p>

          </div>


          <div className="contact-process-grid">

            {process.map((step, index) => (

              <div
                className={`contact-process-card contact-process-card-${index + 1}`}
                key={index}
              >

                <div className="contact-process-card-top">

                  <span>
                    {step.number}
                  </span>

                  <div>
                    {step.icon}
                  </div>

                </div>

                <h3>
                  {step.title}
                </h3>

                <p>
                  {step.text}
                </p>

              </div>

            ))}

          </div>

        </div>

      </section>


      {/* =====================================================
          HELP
      ===================================================== */}

      <section
        ref={helpRef}
        data-animation-section="help"
        className={`contact-help-section ${
          visibleSections.help
            ? "contact-section-visible"
            : ""
        }`}
      >

        <div className="contact-container">

          <div className="contact-help-box">

            <div className="contact-help-icon">
              <CircleHelp size={25} />
            </div>

            <div className="contact-help-content">

              <span className="contact-section-label">
                NOT SURE WHERE TO START?
              </span>

              <h2>
                That's completely fine.
              </h2>

              <p>
                You don't need to have every requirement
                figured out before contacting us. Tell us
                what you're trying to achieve and we'll
                help you identify the right direction.
              </p>

            </div>

            <Link
              to="/services"
              className="contact-help-btn"
            >
              Explore Services
              <ArrowUpRight size={16} />
            </Link>

          </div>

        </div>

      </section>


      {/* =====================================================
          FINAL CTA
      ===================================================== */}

      <section
        ref={finalRef}
        data-animation-section="final"
        className={`contact-final-section ${
          visibleSections.final
            ? "contact-section-visible"
            : ""
        }`}
      >

        <div className="contact-container">

          <div className="contact-final-content">

            <span className="contact-section-label">
              READY WHEN YOU ARE
            </span>

            <h2>
              Have something
              <span> worth building?</span>
            </h2>

            <p>
              Start with a conversation. Tell us what
              you're thinking and we'll take it from there.
            </p>

            <a
              href="#project-enquiry"
              className="contact-final-btn"
            >
              Start a Project
              <ArrowUpRight size={18} />
            </a>

          </div>

        </div>

      </section>

    </div>
  );
}

export default Contact;

